"use client";

import { httpsCallable } from "firebase/functions";
import {
  buildFacelessVideoQuote,
  type CaptionFontFamily,
  type CaptionAnimation,
  type CaptionPosition,
  type CaptionPreset,
  type CaptionTimingMode,
  type FacelessImageStylePresetId,
  type FacelessMotionVideoModelId,
  type FacelessMotionVideoResolution,
  type QuoteResult,
  type SceneDensity,
  type SceneVisualMode,
  type TtsModelId,
} from "@reelmagic/domain";
import { getFirebaseClientServices } from "../firebase/client";

export interface FacelessGenerationRequestPayload {
  captionPosition: CaptionPosition;
  captionPositionPercent: number;
  captionPreset: CaptionPreset;
  captionTimingMode: CaptionTimingMode;
  captionAnimation: CaptionAnimation;
  captionFontFamily: CaptionFontFamily;
  captionTextColor: string;
  captionActiveTextColor: string;
  captionOutlineColor: string;
  captionOutlineWidth: number;
  captionFontSize: number;
  playbackSpeed: number;
  imageStylePresetId: FacelessImageStylePresetId;
  musicTrackId: string;
  motionVideoModelId?: FacelessMotionVideoModelId;
  motionVideoResolution?: FacelessMotionVideoResolution;
  sceneDensity: SceneDensity;
  script: string;
  scriptTargetDurationSeconds: number;
  title: string;
  ttsModelId: TtsModelId;
  visualMode: SceneVisualMode;
  voiceId: string;
}

export interface FacelessGenerationAccepted {
  acceptedAt: string;
  jobId: string;
  quote: QuoteResult;
  videoId: string;
}

function buildLiveFacelessQuote(input: {
  motionVideoModelId?: FacelessMotionVideoModelId;
  motionVideoResolution?: FacelessMotionVideoResolution;
  playbackSpeed?: number;
  sceneDensity: SceneDensity;
  script: string;
  ttsModelId: TtsModelId;
  visualMode?: SceneVisualMode;
}): QuoteResult {
  return buildFacelessVideoQuote(input);
}

export function getFacelessLiveQuote(input: {
  motionVideoModelId?: FacelessMotionVideoModelId;
  motionVideoResolution?: FacelessMotionVideoResolution;
  playbackSpeed?: number;
  sceneDensity: SceneDensity;
  script: string;
  ttsModelId: TtsModelId;
  visualMode?: SceneVisualMode;
}) {
  return buildLiveFacelessQuote(input);
}

export interface GenerateFacelessScriptResponse {
  script: string;
}

export async function generateFacelessScript(input: {
  description: string;
  targetDurationSeconds: number;
}) {
  const { functions } = getFirebaseClientServices();
  const callable = httpsCallable<
    { description: string; targetDurationSeconds: number },
    GenerateFacelessScriptResponse
  >(functions, "generateFacelessScriptForUser", { timeout: 135_000 });
  const response = await callable(input);

  return response.data;
}

export async function requestFacelessGeneration(
  payload: FacelessGenerationRequestPayload,
) {
  const { functions } = getFirebaseClientServices();
  const callable = httpsCallable<
    FacelessGenerationRequestPayload,
    FacelessGenerationAccepted
  >(functions, "requestFacelessGeneration");
  const response = await callable(payload);

  return response.data;
}

export async function retryGeneration(videoId: string) {
  const { functions } = getFirebaseClientServices();
  const callable = httpsCallable<{ videoId: string }, FacelessGenerationAccepted>(
    functions,
    "retryGeneration",
  );
  const response = await callable({ videoId });

  return response.data;
}

export interface RegenerateSceneImageResponse {
  assetUrl: string;
  sceneId: string;
  thumbnailUrl: string;
  updatedAt: string;
  videoId: string;
}

export async function regenerateSceneImage(videoId: string, sceneId: string, prompt: string) {
  const { functions } = getFirebaseClientServices();
  const callable = httpsCallable<{ prompt: string; sceneId: string; videoId: string; operationId: string }, { operationId: string }>(functions, "regenerateSceneImage");
  const response = await callable({ prompt, sceneId, videoId, operationId: crypto.randomUUID() });
  const getResult = httpsCallable<{ operationId: string }, { status: string; error: string | null; assetUrl: string | null }>(functions, "getImageOperationResult");
  const deadline = Date.now() + 30 * 60_000;
  while (Date.now() < deadline) {
    const { data } = await getResult({ operationId: response.data.operationId });
    if (data.status === "failed") throw new Error(data.error ?? "Image generation failed.");
    if (data.status === "completed" && data.assetUrl) return { assetUrl: data.assetUrl, thumbnailUrl: data.assetUrl, sceneId, videoId, updatedAt: new Date().toISOString() };
    await new Promise(resolve => setTimeout(resolve, 3000));
  }
  throw new Error("Image generation is taking longer than expected. Check the scene again later.");
}

export interface RegenerateSceneMotionResponse {
  jobId: string;
  sceneId: string;
  status: string;
  updatedAt: string;
  videoId: string;
}

export interface RegenerateSceneMotionOptions {
  motionVideoModelId?: FacelessMotionVideoModelId;
  motionVideoResolution?: FacelessMotionVideoResolution;
}

export async function regenerateSceneMotion(
  videoId: string,
  sceneId: string,
  prompt?: string,
  options?: RegenerateSceneMotionOptions,
) {
  const { functions } = getFirebaseClientServices();
  const callable = httpsCallable<
    {
      motionVideoModelId?: FacelessMotionVideoModelId;
      motionVideoResolution?: FacelessMotionVideoResolution;
      prompt?: string;
      sceneId: string;
      videoId: string;
    },
    RegenerateSceneMotionResponse
  >(functions, "regenerateSceneMotion");
  const response = await callable({
    ...(options?.motionVideoModelId
      ? { motionVideoModelId: options.motionVideoModelId }
      : {}),
    ...(options?.motionVideoResolution
      ? { motionVideoResolution: options.motionVideoResolution }
      : {}),
    ...(prompt ? { prompt } : {}),
    sceneId,
    videoId,
  });

  return response.data;
}
